const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/// The URL prefix that separates the customer world from the internal one — the portal's auth
/// screens and the customer shell's router both hang off it. Defined here, the one portal module
/// with no app imports of its own, so anything can read it without an import cycle.
export const PORTAL_BASENAME = '/portal';

// sessionStorage, matching the deliberate choice MSAL is configured with (authConfig.js): tokens
// die with the browser tab rather than lingering on a shared county machine. The cost is that a
// customer who closes the tab signs in again, which is the same deal internal users already have.
//
// Not an httpOnly cookie, which would otherwise be the stronger option: apps/web (Static Web Apps)
// and the API (App Service) are different origins, so the refresh cookie would be third-party and
// silently dropped by Safari and by Chrome's third-party cookie restrictions. A portal that fails
// to stay signed in on some browsers and not others is a worse outcome than storage the page's own
// scripts can read — and an XSS able to read sessionStorage could just as easily call the API
// directly with the cookie attached anyway. See ADR 0035.
const ACCESS_TOKEN_KEY = 'psops.customer.accessToken';
const REFRESH_TOKEN_KEY = 'psops.customer.refreshToken';
const EXPIRES_AT_KEY = 'psops.customer.expiresAt';

// localStorage, deliberately unlike the three keys above: a "recognized device" has to outlive the
// tab to mean anything, where the tokens above are supposed to die with it (see the file-top
// comment). Carries no authority by itself -- it's a lookup key against a row the account already
// trusted, not a credential -- so there's nothing worse than an unnecessary OTP email if it's lost.
const DEVICE_ID_KEY = 'psops.customer.deviceId';

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined; // private mode -- every sign-in just gets the email step
  }
}

// Refresh this long before the access token actually expires, so a request never leaves with a
// token that dies in flight.
const REFRESH_SKEW_MS = 60 * 1000;

const listeners = new Set();

function read(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // private mode
  }
}

function write(key, value) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* private mode — the session simply won't survive a reload */
  }
}

function store(tokens) {
  write(ACCESS_TOKEN_KEY, tokens.accessToken);
  write(REFRESH_TOKEN_KEY, tokens.refreshToken);
  write(EXPIRES_AT_KEY, String(Date.now() + tokens.expiresIn * 1000));
}

function clear() {
  write(ACCESS_TOKEN_KEY, null);
  write(REFRESH_TOKEN_KEY, null);
  write(EXPIRES_AT_KEY, null);
}

function notify() {
  listeners.forEach((listener) => listener());
}

/// True when this tab is running as a customer session. Read by apiClient to decide which kind of
/// token to attach, and by index.js to decide which shell to boot.
export function hasCustomerSession() {
  return Boolean(read(REFRESH_TOKEN_KEY));
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function postJson(path, body) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let detail = '';
    try {
      const parsed = await response.json();
      const message = parsed?.message;
      detail = Array.isArray(message) ? message.join('; ') : message || '';
    } catch {
      /* no JSON body */
    }
    const error = new Error(detail || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

/// Resolves to `{ otpRequired: true }` on an unrecognized device (Phase D, ADR 0037) instead of
/// signing in -- the caller's job is to show the code-entry step and call `verifyOtp`.
export async function signIn(email, password) {
  const result = await postJson('/auth/customer/login', { email, password, deviceId: getDeviceId() });
  if (result.otpRequired) return result;
  store(result);
  notify();
  return { user: result.user };
}

export async function verifyOtp(email, code) {
  const tokens = await postJson('/auth/customer/otp/verify', { email, code, deviceId: getDeviceId() });
  store(tokens);
  notify();
  return { user: tokens.user };
}

export function resendOtp(email) {
  return postJson('/auth/customer/otp/resend', { email });
}

export async function signOut() {
  const refreshToken = read(REFRESH_TOKEN_KEY);
  clear();
  notify();
  // After clearing, and failure ignored: the local session is already gone, so a network error on
  // the way to revoking the server side must not leave the user looking signed in.
  if (refreshToken) {
    await postJson('/auth/customer/logout', { refreshToken }).catch(() => {});
  }
}

export function requestPasswordReset(email) {
  return postJson('/auth/customer/forgot-password', { email });
}

export function acceptInvitation(token, password) {
  return postJson('/auth/customer/accept-invitation', { token, password });
}

export function resetPassword(token, password) {
  return postJson('/auth/customer/reset-password', { token, password });
}

/// Checks an emailed link before the screen renders, so it can greet the right person and tell
/// "expired" apart from "already used". Never throws — an unreachable API reads as an invalid link,
/// which is what the screen would show anyway.
export async function checkLink(kind, token) {
  const path = kind === 'invite' ? 'invitation' : 'reset';
  try {
    const response = await fetch(
      `${API_BASE_URL}/auth/customer/${path}?token=${encodeURIComponent(token)}`,
    );
    if (!response.ok) return { valid: false };
    return await response.json();
  } catch {
    return { valid: false };
  }
}

// A single in-flight refresh shared by every caller.
//
// This is not just an optimization. The refresh token rotates on use and the API treats a token
// presented twice as theft — it revokes the whole session family (customer-token.service.ts). Two
// components refreshing in parallel would present the same token twice and sign the user out for
// no reason, so concurrent callers must await one promise rather than each starting their own.
let inFlightRefresh = null;

async function refresh() {
  const refreshToken = read(REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error('Not signed in');

  try {
    const tokens = await postJson('/auth/customer/refresh', { refreshToken });
    store(tokens);
    return tokens.accessToken;
  } catch (err) {
    // A rejected refresh token is terminal — expired, revoked, or a family that tripped reuse
    // detection. Clearing here is what makes CustomerAuthGate fall back to the sign-in screen
    // rather than looping on a token that will never work again.
    if (err.status === 401) {
      clear();
      notify();
    }
    throw err;
  }
}

/**
 * Returns a valid access token, refreshing first if the current one is near expiry.
 *
 * Callers should treat a throw as "the session is over" — by the time it throws, local state has
 * already been cleared and the gate is re-rendering to the sign-in screen.
 */
export async function getCustomerAccessToken() {
  const accessToken = read(ACCESS_TOKEN_KEY);
  const expiresAt = Number(read(EXPIRES_AT_KEY)) || 0;

  if (accessToken && Date.now() < expiresAt - REFRESH_SKEW_MS) return accessToken;

  if (!inFlightRefresh) {
    inFlightRefresh = refresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}
